import React, { useEffect, useState, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import hashtagsService from '../../api/hashtags';
import { useUniversity } from '../../contexts/UniversityContext';

// Tiempo mínimo entre recargas manuales (30 segundos)
const REFRESH_COOLDOWN = 30000;

const Container = styled.div`
  background: ${props => props.theme.card};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  padding: 15px;
  margin-bottom: 20px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  
  @media (max-width: 992px) {
    margin-left: 0;
    width: 100%;
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.$isCollapsed ? '10px 12px' : '12px'};
    margin-bottom: 15px;
    margin-left: 0;
    width: 100%;
    max-height: ${props => props.$isCollapsed ? '48px' : 'none'};
    transition: max-height 0.3s ease, padding 0.3s ease;
    overflow: hidden;
  }
  
  @media (max-width: 480px) {
    padding: ${props => props.$isCollapsed ? '8px 10px' : '10px'};
    margin-bottom: 10px;
  }
`;

const Title = styled.h3`
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 18px;
  color: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: ${props => props.$isCollapsed ? '0' : '12px'};
    cursor: pointer;
  }
  
  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: ${props => props.$isCollapsed ? '0' : '10px'};
  }
`;

const DropdownToggle = styled.div`
  display: none;
  margin-left: auto;
  align-items: center;
  gap: 5px;
  color: ${props => props.theme.primary};
  padding-left: 15px;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  @media (max-width: 480px) {
    padding-left: 10px;
  }
`;

const DropdownText = styled.span`
  font-size: 13px;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const DropdownIcon = styled.span`
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  min-height: 220px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  width: 100%;
  transition: min-height 0.3s ease, max-height 0.3s ease, opacity 0.3s ease;
  padding: 5px 2px;
  
  /* Estilizar scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollThumb};
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    min-height: ${props => props.$isCollapsed ? '0' : '180px'};
    max-height: ${props => props.$isCollapsed ? '0' : '300px'};
    overflow: hidden;
    transition: all 0.3s ease;
    height: ${props => props.$isCollapsed ? '0' : 'auto'};
    opacity: ${props => props.$isCollapsed ? '0' : '1'};
    visibility: ${props => props.$isCollapsed ? 'hidden' : 'visible'};
    padding: 3px 0;
  }

  @media (max-width: 480px) {
    min-height: ${props => props.$isCollapsed ? '0' : '150px'};
    max-height: ${props => props.$isCollapsed ? '0' : '250px'};
    padding: 2px 0;
  }

  @media (max-height: 600px) {
    min-height: ${props => props.$isCollapsed ? '0' : '120px'};
    max-height: ${props => props.$isCollapsed ? '0' : 'calc(100vh - 200px)'};
  }
`;

// Animación de aterrizaje para los hashtags
const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Item = styled.li`
  padding: 10px 0;
  border-bottom: 1px solid ${props => props.theme.divider};
  animation: ${fadeInDown} 0.3s ease forwards;
  animation-delay: ${props => props.$index * 0.08}s;
  opacity: 0;
  
  @media (max-width: 768px) {
    padding: 8px 0;
  }
  
  @media (max-width: 480px) {
    padding: 6px 0;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const HashtagLink = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: ${props => props.theme.hashtag};
  gap: 10px;
  width: 100%;
  padding: 0 2px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Hashtag = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 85%;
  flex: 1;
  
  @media (max-width: 480px) {
    font-size: 14px;
    max-width: 75%;
  }
`;

const Count = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
  margin-left: auto;
  padding-left: 8px;
  flex-shrink: 0;
  text-align: right;
  min-width: 25px;
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${props => props.theme.textSecondary};
  padding: 10px 0;
  font-size: 14px;
  min-height: 220px;
  max-height: calc(100vh - 280px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$loading ? 0 : 1};
  transition: opacity 0.3s ease, min-height 0.3s ease, max-height 0.3s ease;
  width: 100%;
  word-break: break-word;
  
  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 0;
    min-height: ${props => props.$isCollapsed ? '0' : '180px'};
    max-height: ${props => props.$isCollapsed ? '0' : '300px'};
    height: ${props => props.$isCollapsed ? '0' : 'auto'};
    opacity: ${props => props.$isCollapsed ? '0' : '1'};
    overflow: hidden;
    transition: opacity 0.3s ease, min-height 0.3s ease, max-height 0.3s ease, height 0.3s ease;
  }

  @media (max-height: 600px) {
    min-height: ${props => props.$isCollapsed ? '0' : '120px'};
    max-height: ${props => props.$isCollapsed ? '0' : 'calc(100vh - 200px)'};
    padding: 5px 0;
  }
`;

const EmptySpace = styled.div`
  min-height: 220px;
  max-height: calc(100vh - 280px);
  background: ${props => props.theme.card};
  border-radius: 4px;
  animation: shimmer 2s infinite linear;
  background: linear-gradient(
    to right,
    ${props => props.theme.card} 0%,
    ${props => props.theme.backgroundLight} 20%,
    ${props => props.theme.card} 40%,
    ${props => props.theme.card} 100%
  );
  background-size: 800px 100%;
  
  @media (max-width: 768px) {
    min-height: ${props => props.$isCollapsed ? '0' : '180px'};
    max-height: ${props => props.$isCollapsed ? '0' : '300px'};
    height: ${props => props.$isCollapsed ? '0' : 'auto'};
    opacity: ${props => props.$isCollapsed ? '0' : '1'};
    overflow: hidden;
  }

  @media (max-height: 600px) {
    min-height: ${props => props.$isCollapsed ? '0' : '120px'};
    max-height: ${props => props.$isCollapsed ? '0' : 'calc(100vh - 200px)'};
  }
  
  @keyframes shimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 4px 8px;
  margin-left: 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  
  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : props.theme.backgroundLight};
  }
  
  &:disabled {
    pointer-events: none;
  }
`;

const CooldownCircle = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  z-index: 1;
`;

const TrendingHashtags = ({ onHashtagClick }) => {
  const [hashtags, setHashtags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedUniversity, currentUniversity } = useUniversity();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  
  // Estado para el sistema de limitación de frecuencia
  const [refreshCooldown, setRefreshCooldown] = useState(0);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const cooldownInterval = useRef(null);
  const initialLoadRef = useRef(false);
  const lastUniversityRef = useRef(selectedUniversity);
  const mountCountRef = useRef(0);
  const effectRunCountRef = useRef(0);
  
  // Log de montaje
  useEffect(() => {
    mountCountRef.current += 1;
    return () => {
    };
  }, []);
  
  // Función para cargar hashtags
  const loadHashtags = useCallback(async (forceRefresh = false) => {
    if (!currentUniversity) {
      setHashtags([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const data = await hashtagsService.getTrendingHashtags(5, selectedUniversity, forceRefresh);
      setHashtags(Array.isArray(data) ? data : []);
      setError(null);
    } catch (error) {
      setError('No se pudieron cargar los hashtags en tendencia');
    } finally {
      setLoading(false);
    }
  }, [currentUniversity, selectedUniversity]);
  
  // Efecto principal para cargar hashtags y manejar actualizaciones
  useEffect(() => {
    effectRunCountRef.current += 1;
    
    // Solo cargar si:
    // 1. Es la primera carga (initialLoadRef.current es false)
    // 2. O si la universidad ha cambiado realmente
    const shouldLoad = !initialLoadRef.current || lastUniversityRef.current !== selectedUniversity;
    
    if (shouldLoad) {
      // Solo cargar si es la primera ejecución del efecto o si la universidad ha cambiado
      if (effectRunCountRef.current === 1 || lastUniversityRef.current !== selectedUniversity) {
        loadHashtags();
        initialLoadRef.current = true;
        lastUniversityRef.current = selectedUniversity;
      }
    }
    
    // Invalidar cache para la universidad anterior si cambia
    if (selectedUniversity && lastUniversityRef.current !== selectedUniversity) {
      hashtagsService.invalidateUniversityHashtags(selectedUniversity);
    }
    
    // Intervalo para actualizar hashtags periódicamente (cada 5 minutos)
    const intervalId = setInterval(() => {
      loadHashtags();
    }, 5 * 60 * 1000);
    
    return () => {
      clearInterval(intervalId);
      if (cooldownInterval.current) {
        clearInterval(cooldownInterval.current);
      }
    };
  }, [loadHashtags, selectedUniversity]);
  
  // Función para forzar recarga manual con limitación de frecuencia
  const handleRefresh = () => {
    if (refreshDisabled) return;
    
    // Deshabilitar el botón e iniciar el temporizador de enfriamiento
    setRefreshDisabled(true);
    setRefreshCooldown(REFRESH_COOLDOWN);
    
    // Iniciar intervalo para actualizar el tiempo restante
    if (cooldownInterval.current) {
      clearInterval(cooldownInterval.current);
    }
    
    cooldownInterval.current = setInterval(() => {
      setRefreshCooldown(prev => {
        const newValue = prev - 1000;
        if (newValue <= 0) {
          clearInterval(cooldownInterval.current);
          setRefreshDisabled(false);
          return 0;
        }
        return newValue;
      });
    }, 1000);
    
    // Realizar la recarga
    loadHashtags(true);
  };
  
  // Calcular el porcentaje del temporizador para la animación
  const cooldownPercentage = refreshDisabled ? (refreshCooldown / REFRESH_COOLDOWN) * 100 : 0;
  
  // Función para manejar el clic en un hashtag
  const handleHashtagClick = (e, hashtagName) => {
    e.preventDefault();
    if (onHashtagClick) {
      // Usar la función proporcionada por el componente padre
      onHashtagClick(hashtagName);
    } else {
      // Fallback al comportamiento anterior si no hay prop
      window.location.href = `/?hashtag=${hashtagName}`;
    }
  };

  // Función para alternar el colapso en móvil
  const toggleCollapse = () => {
    if (window.innerWidth <= 768) {
      setIsCollapsed(!isCollapsed);
    }
  };
  
  // Renderizar el contenido adecuado según el estado
  const renderContent = () => {
    if (!currentUniversity) {
      return <EmptyState $isCollapsed={isCollapsed}>Selecciona una universidad para ver los hashtags en tendencia</EmptyState>;
    }
    
    if (loading) {
      return <EmptySpace $isCollapsed={isCollapsed} />;
    }
    
    if (error) {
      return <EmptyState $isCollapsed={isCollapsed}>{error}</EmptyState>;
    }
    
    if (hashtags.length > 0) {
      return (
        <List $isCollapsed={isCollapsed}>
          {hashtags.map((hashtag, index) => (
            <Item key={index} $index={index}>
              <HashtagLink 
                to="/"
                onClick={(e) => handleHashtagClick(e, hashtag.hashtag)}
              >
                <Hashtag>#{hashtag.hashtag}</Hashtag>
                <Count>{hashtag.count}</Count>
              </HashtagLink>
            </Item>
          ))}
        </List>
      );
    }
    
    return <EmptyState $loading={loading} $isCollapsed={isCollapsed}>No hay hashtags. Crea uno #campus</EmptyState>;
  };
  
  return (
    <Container $isCollapsed={isCollapsed}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Title onClick={toggleCollapse} $isCollapsed={isCollapsed}>
          🔥 Tendencias
          <DropdownToggle>
            <DropdownText>{isCollapsed ? 'Ver hashtags' : 'Ocultar hashtags'}</DropdownText>
            <DropdownIcon $isOpen={!isCollapsed}>▼</DropdownIcon>
          </DropdownToggle>
        </Title>
        <RefreshButton 
          onClick={handleRefresh} 
          disabled={refreshDisabled || loading}
          title={refreshDisabled ? `Espera ${Math.ceil(refreshCooldown / 1000)}s para actualizar` : "Actualizar tendencias"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
          </svg>
          
          {refreshDisabled && (
            <CooldownCircle viewBox="0 0 36 36">
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="rgba(23, 162, 184, 0.5)"
                strokeWidth="3"
                strokeDasharray={`${cooldownPercentage}, 100`}
                transform="rotate(90, 18, 18)"
              />
            </CooldownCircle>
          )}
        </RefreshButton>
      </div>
      
      {renderContent()}
    </Container>
  );
};

export default TrendingHashtags;
